/**
 * `PartialDateMemo` — a memo of cell-date parses by their string, held by the
 * one owner that parses them (judge P8). A seeded or fixture sheet's dates
 * reach the SDK as strings and are parsed at every hop — the seed check, the
 * seeded backend, the wire cells built from it, the rows-side hydrate, the
 * fixture backend, the server-fetch ingest — and each hop parsed every cell.
 * Dates repeat heavily across rows (one string per day of the window), so a
 * hop that parses through its own memo parses each distinct string once and
 * hands the parsed value to every cell that names it.
 *
 * Same answers as the functions it wraps: `parse` returns the same `Result`
 * as `NaiveDate.Partial.parse` (an error is not remembered: it is parsed
 * again and fails the same way), `parseOptional` the same value or `null` as
 * `NaiveDate.Partial.parseOptional`, `semanticTime` the same string as
 * `SemanticTime.parse` (and throws the same way). A `NaiveDate.Partial` is
 * immutable (its lazy `cached` fields only memoize its own derivations), so
 * cells may share one. Bounded: past `capacity` distinct strings it starts
 * over. Not module state: each owner (a hydrate call, a backend, a data
 * source) holds its own.
 */

import type { Option } from "../core/option.ts";
import { NaiveDate, Result, SemanticTime } from "@tento-chrono";

export class PartialDateMemo {
  readonly #capacity: number;
  readonly #partials = new Map<string, Result<NaiveDate.Partial>>();
  readonly #semanticTimes = new Map<string, SemanticTime>();

  constructor(capacity: number) {
    this.#capacity = capacity;
  }

  /** `NaiveDate.Partial.parse(s)`, parsing each distinct `s` once. */
  parse(s: string): Result<NaiveDate.Partial> {
    const hit = this.#partials.get(s);
    if (hit != null) return hit;
    const parsed = NaiveDate.Partial.parse(s);
    if (parsed.isOk) this.#remember(this.#partials, s, parsed);
    return parsed;
  }

  /** `NaiveDate.Partial.parseOptional(s)`, parsing each distinct `s` once. */
  parseOptional(s: string): Option<NaiveDate.Partial> {
    const hit = this.#partials.get(s);
    if (hit != null) return hit.asOk() ?? null;
    const parsed = NaiveDate.Partial.parseOptional(s) ?? null;
    if (parsed != null) this.#remember(this.#partials, s, Result.ok(parsed));
    return parsed;
  }

  /** `SemanticTime.parse(raw)`, parsing each distinct `raw` once. */
  semanticTime(raw: string): SemanticTime {
    const hit = this.#semanticTimes.get(raw);
    if (hit != null) return hit;
    const parsed = SemanticTime.parse(raw);
    this.#remember(this.#semanticTimes, raw, parsed);
    return parsed;
  }

  #remember<V>(memo: Map<string, V>, key: string, value: V): void {
    if (memo.size >= this.#capacity) memo.clear();
    memo.set(key, value);
  }
}

/** Types and values that go with `PartialDateMemo`: `CAPACITY`. */
export namespace PartialDateMemo {
  /** The distinct strings an owner's memo keeps before it starts over. */
  export const CAPACITY = 8192;
}
